import { push, GO_BACK, replace } from 'redux-little-router';
import size from 'lodash/size';
import {presentSubviewIdentifier, presentModelUrl} from "~/statePresenter";
import {
    getLoadedModels, getModelNameOrNull, getModels, getParentId, getSetCurrentModelId,
    getSubview,
    getSubviews,
} from "~/stateGetter";
import getConfig from "~/config";
import {downloadWebsailFile, getDownloadUrl} from "~/backend/backendUrl";
import {pushHelpPath} from "~/actions/help_actions";
import lexModelPathName from '~/navigation/helpers/lexModelPathName';

function goBack(options) {
    return { type: GO_BACK, payload: {options} };
};

export function pushModelPath(modelId, path, options, ...args) {
    return (dispatch, getState) => {
        let state = getState(),
            loadedModels = getLoadedModels(state),
            models = getModels(state, modelId);

        let modelUrl = presentModelUrl({loadedModels, modelId, models, ...options});
        path = getNormalizedPath(path);
        path.pathname = `${modelUrl}${path.pathname}`;
        const pushAction = options?.replace ? replace : push;
        return dispatch(pushAction(path, ...args));
    }
}

export function pushCurrentModelHelpPath(...args) {
    return (dispatch, getState) => {
        let state = getState(),
            modelId = getSetCurrentModelId(state),
            modelName = getModelNameOrNull(state, modelId);

        return dispatch(pushHelpPath(modelName, {}, ...args));
    }
}

export function pushCurrentModelPath(path, options, ...args) {
    return (dispatch, getState) => {
        let state = getState(),
            modelId = getSetCurrentModelId(state);

        return dispatch(pushModelPath(modelId, getNormalizedPath(path), options, ...args));
    }
}

export function pushSubview(modelId, subviewName, path = '', ...args) {
    return (dispatch, getState) => {
        let state = getState(),
            subviews = getSubviews(state, modelId),
            subview = getSubview(state, modelId, subviewName),
            identifier = presentSubviewIdentifier({subviews, subview});

        const hasQuery =path.query && size(path.query) > 0;

        path = getNormalizedPath(path);
        path.pathname = hasQuery ? "" : `/${identifier}${path.pathname}`;

        dispatch(pushCurrentModelPath(path, { includeCurrent: true, includeItemId: true }, ...args));
    };
}

export function pushClose({goBack: shouldGoBack = true, ...options} = {}, ...args) {
    return (dispatch, getState) => {
        let state = getState(),
            modelId = getSetCurrentModelId(state),
            parentModelId = getParentId(state, modelId);

        if (shouldGoBack) return dispatch(goBack(options));

        if(parentModelId) {
            return dispatch(pushModelPath(parentModelId, '', { includeCurrent: true, includeItemId: true }, options, ...args));
        }

        return dispatch(pushModelPath(modelId, '', { includeCurrent: true }, options, ...args));
    };
}

export function smartGoto(url, options = {}, routeOptions = {}) {
    return (dispatch, getState) => {
        let match = url.match(/\/admin([^?]+)/);

        const openInNewTab = routeOptions.openInNewTab ?? true;
        const shouldReplace = routeOptions.replace;

        if(match) {
            const dispatchAction = routeOptions.replaceIfInternal || shouldReplace ? replace : pushFragments;
            const preparedURL = new URL(url, window.location.origin);
            return dispatch(dispatchAction(match[1] + preparedURL.search, options));
        }

        if (!openInNewTab) {
            if (shouldReplace) window.location.replace(url);
            else window.location.href = url;
            return;
        }

        const openHandle = window.open(url, '_blank');
        // popups can be blocked and in that case the window is null
        if(openHandle) {
            openHandle.focus();
        } else {
            window.location.href = url;
        }
    };
}

export function openActionResult(result, options = {}) {
    return (dispatch, getState) => {
        if(result.downloadId) {
            let supportedMimeTypes = getConfig().openMimeTypes;
            if(result.mimeType && supportedMimeTypes.filter(m => result.mimeType.indexOf(m) > -1).length) {
                return window.open(getDownloadUrl(result.downloadId), '_blank');
            }

            return downloadWebsailFile(result.downloadId);
        }

        if(result.redirect) {
            return dispatch(smartGoto(result.redirect, options));
        }
    }
}


export function pushWithAbsoluteLinks(url, options, routeOptions) {
    return (dispatch) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            return dispatch(smartGoto(url, options, routeOptions))
        }

        return dispatch(push(url, routeOptions))
    }
}

export function pushFragments(url, options) {
    const preparedURL = new URL(url, window.location.origin);
    url = preparedURL.pathname;
    const {replaceRoot, ...restOptions} = options ?? {};

    return (dispatch, getState) => {
        const state = getState();
        const query = options?.persistQuery ? state.router.query : {};

        lexModelPathName(url).reduce((previous, lexedPath, i, self) => {
            let path = lexedPath.itemId ? `${previous}/${lexedPath.modelName}/${lexedPath.itemId}` : `${previous}/${lexedPath.modelName}`;
            const isLastFragment = (self[self.length - 1] === lexedPath);
            const preparedFragment = new URL(path, window.location.origin);

            if (isLastFragment && preparedURL.searchParams.size) {
                path = {
                    pathname: preparedFragment.pathname,
                    query: {
                        ...query,
                        ...Object.fromEntries(preparedURL.searchParams),
                    },
                }
            }


            if (replaceRoot && i === 0) {
                dispatch(replace('/'));
                if (lexedPath.itemId) dispatch(push(lexedPath.modelName, restOptions));
            }

            dispatch(push(path, restOptions));

            return path.pathname ?? path
        }, '');
    }
}

export function getNormalizedPath(path) {
    const pathObject = typeof path === "string" ? {
        pathname: path
    } : path
    return pathObject
}